import { Box, Divider, Grid, RadioGroup, Typography } from '@mui/material';
import RadioCard from '@acorn-portal/shared/src/components/radioCard';
import { FC, ChangeEvent } from 'react';
import { FinancePlanWithDeposit, RenewalRatingResponse } from '@acorn-portal/shared/src/services/renewalsApi';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import MonthlyOption from './MonthlyOption';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';

export interface IPaymentOptionValue {
	method?: string;
	deposit?: boolean;
}

interface IPaymentOptions {
	paymentOptions: RenewalRatingResponse;
	value?: IPaymentOptionValue;
	onChange: (values: IPaymentOptionValue) => void;
}

const PaymentOptionsSelector: FC<IPaymentOptions> = ({ paymentOptions, value, onChange }) => {
	const policy = useAppSelector((state) => state.fullPolicy);

	const handleDepositChange = (deposit: string) => {
		onChange({
			method: 'finance',
			deposit: deposit === 'yes',
		});
	};

	const handleMethodChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!paymentOptions.financePlanWithDeposit) {
			onChange({ deposit: false, method: event.target.value });
		} else if (policy.scheme === 'Acorn6MTX' && event.target.value === 'finance') {
			onChange({ deposit: true, method: event.target.value });
			return;
		} else {
			onChange({ deposit: undefined, method: event.target.value });
			return;
		}
	};

	const getDepositValue = () => {
		if (value?.deposit === undefined) return undefined;

		return value?.deposit ? 'yes' : 'no';
	};

	return (
		<Box>
			<RadioGroup
				onChange={handleMethodChange}
				aria-labelledby='demo-radio-buttons-group-label'
				defaultValue='monthly'
				name='radio-buttons-group'
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', md: 'row' },
						gap: { xs: 1, md: 4 },
						paddingX: { md: 6 },
					}}
				>
					{paymentOptions?.financePlanWithoutDeposit && (
						<MonthlyOption
							title='Pay Monthly'
							name='finance'
							depositValue={getDepositValue()}
							insuranceTaxPercentage={paymentOptions.insurancePremiumTaxPercentage}
							paymentDay={
								(value?.deposit
									? (paymentOptions.financePlanWithDeposit?.existingDirectDebitPaymentDay ??
										paymentOptions.financePlanWithDeposit?.directDebitPaymentSchedule.collectionDay)
									: (paymentOptions.financePlanWithoutDeposit?.existingDirectDebitPaymentDay ??
										paymentOptions.financePlanWithoutDeposit.directDebitPaymentSchedule.collectionDay)) ?? 0
							}
							policyAdministrationFee={paymentOptions.policyAdministrationFee}
							selected={value?.method === 'finance'}
							annualPremium={paymentOptions.annualPremium}
							withDeposit={!!paymentOptions.financePlanWithDeposit}
							rates={
								value?.deposit
									? (paymentOptions.financePlanWithDeposit as FinancePlanWithDeposit)
									: paymentOptions.financePlanWithoutDeposit
							}
							onDepositChange={handleDepositChange}
						/>
					)}
					<RadioCard
						title={policy.isShortTermPolicy ? 'Pay In Full' : 'Pay Annually'}
						selected={value?.method === 'total'}
						value='total'
					>
						<Grid container sx={{ flexDirection: 'column', width: '100%' }}>
							<Grid>
								<Typography sx={{ fontSize: 14, mt: 2 }}>Amount Due Today</Typography>

								<Typography
									sx={{
										fontWeight: 'bold',
										fontSize: 35,
										color: 'secondary.main',
									}}
								>
									{formatCurrency(paymentOptions?.annualPremium)}
								</Typography>
								{!policy.isShortTermPolicy && (
									<Grid container sx={{ mt: 2 }}>
										<Grid sx={{ flex: 1 }}>
											<Typography sx={{ fontSize: 14 }}>Annual Premium</Typography>
											<Typography sx={{ fontSize: 14 }}>{formatCurrency(paymentOptions.annualPremium)}</Typography>
										</Grid>
										<Grid sx={{ flex: 1 }}>
											<Typography sx={{ fontSize: 14 }}>Total</Typography>
											<Typography sx={{ fontSize: 14 }}>{formatCurrency(paymentOptions.annualPremium)}</Typography>
										</Grid>
									</Grid>
								)}
								<Divider sx={{ my: 2 }} />
								<Typography
									sx={{
										fontSize: 14,
									}}
								>
									Prices include insurance premium tax of {paymentOptions.insurancePremiumTaxPercentage}% and a
									non-refundable policy administration fee of {formatCurrency(paymentOptions.policyAdministrationFee)}
								</Typography>
							</Grid>
						</Grid>
					</RadioCard>
				</Box>
			</RadioGroup>
		</Box>
	);
};

export default PaymentOptionsSelector;
